import { NavigationGuard, RouteConfig } from 'vue-router';
import { app } from '@src/store/modules/app';
import { autoInvest } from '@store/modules/auto-invest';
import { entityTypePath } from '@utils/settings';
import { investmentEntity } from '@store/modules/investment-entity';

const autoInvestEnableFlowRouteGuard: NavigationGuard = async (to, from, next) => {
	if (investmentEntity.hasActiveRecurringInvestmentSchedule) {
		next({
			name: 'auto-invest-view',
			params: {
				investmentEntityId: investmentEntity.investmentEntityId,
				entityType: entityTypePath(investmentEntity.entityTypeLabel)
			}
		});
	} else if (!investmentEntity.eligibleForRecurringInvestment) {
		next({ name: 'account-overview', replace: true });
		app.ADD_TOAST({
			type: 'error',
			message: 'Your investment account is not eligible for Auto-Invest.'
		});
	} else {
		const plaidRedirect = to.query['oauth_state_id'];

		if (plaidRedirect) {
			next({ query: { oauth_state_id: plaidRedirect } });
		} else {
			next();
		}
	}
};

const sameAutoInvestSessionCheck: NavigationGuard = async (to, from, next) => {
	if (investmentEntity.hasActiveRecurringInvestmentSchedule) {
		next({
			name: 'auto-invest-view',
			params: {
				investmentEntityId: investmentEntity.investmentEntityId,
				entityType: entityTypePath(investmentEntity.entityTypeLabel)
			}
		});
	} else if (!autoInvest.autoInvestSchedule) {
		next({ name: 'auto-invest-enable-new' });
	} else {
		next();
	}
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const autoInvestEnableRoutes: Array<RouteConfig> = [
	{
		path: `/account/auto-invest`,
		name: 'auto-invest-enable',
		beforeEnter: autoInvestEnableFlowRouteGuard,
		component: () =>
			import(/* webpackChunkName: "auto-invest-enable" */ '@views/auto-invest/auto-invest-enable.vue'),
		redirect: { name: 'auto-invest-enable-new' },
		children: [
			{
				path: `new`,
				name: 'auto-invest-enable-new',
				beforeEnter: async (to, from, next) => {
					if (investmentEntity.hasActiveRecurringInvestmentSchedule) {
						next({
							name: 'auto-invest-view',
							params: {
								investmentEntityId: investmentEntity.investmentEntityId,
								entityType: entityTypePath(investmentEntity.entityTypeLabel)
							}
						});
					} else {
						await Promise.all([
							investmentEntity.storeBankAccounts(),
							autoInvest.getFrequenciesAndAmounts(),
							autoInvest.getAgreements()
						]);

						const plaidRedirect = to.query['oauth_state_id'];

						if (plaidRedirect) {
							next({ query: { oauth_state_id: plaidRedirect } });
						} else {
							next();
						}
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "auto-invest-enable-new" */ '@views/auto-invest/auto-invest-enable-new.vue'
					)
			},
			{
				path: `agreements`,
				name: 'auto-invest-enable-agreements',
				beforeEnter: sameAutoInvestSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "auto-invest-enable-agreements" */ '@views/auto-invest/auto-invest-enable-agreements.vue'
					)
			},
			{
				path: `review`,
				name: 'auto-invest-enable-review',
				beforeEnter: sameAutoInvestSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "auto-invest-enable-review" */ '@views/auto-invest/auto-invest-enable-review.vue'
					)
			},
			{
				path: `success`,
				name: 'auto-invest-enable-success',
				beforeEnter: async (to, from, next) => {
					if (investmentEntity.autoInvestSchedule) {
						next();
					} else {
						next('auto-invest-enable-new');
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "auto-invest-enable-success" */ '@views/auto-invest/auto-invest-enable-success.vue'
					)
			}
		]
	}
];

export default autoInvestEnableRoutes;
